import { useMemo, useState } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useListProductSources } from '@/shared/hooks/api/productSource'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ComplianceSubPageHeader } from '@/features/compliance-studio/machinery-directive/components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '@/features/compliance-studio/machinery-directive/components/ComplianceSubPageTitle'
import {
  useGetCraUserInformation,
  type CraAnnexIIRequirement,
} from './api'
import { CraRequirementItem } from './CraRequirementItem'
import { useValidateCraUserInformation } from './useValidateCraUserInformation'

function RequirementList({
  items,
  validationItems,
  processingId,
  nested,
}: {
  items: CraAnnexIIRequirement[]
  validationItems: ReturnType<
    typeof useValidateCraUserInformation
  >['validationItems']
  processingId: string | null
  nested?: boolean
}) {
  return (
    <ul className='list-none space-y-2 divide-y pb-2'>
      {items.map((item) => {
        const children = item.children ?? []
        const hasChildren = children.length > 0
        let itemClassName = 'px-4 py-2 text-sm'
        if (nested) {
          itemClassName = 'px-4 py-2 ps-10 text-sm'
        }
        return (
          <li key={item.id} className={itemClassName}>
            {hasChildren && (
              <div>
                <p className='flex gap-2 text-muted-foreground'>
                  <span className='shrink-0 font-medium'>{item.number}</span>
                  <span>{item.text}</span>
                </p>
                <RequirementList
                  items={children}
                  validationItems={validationItems}
                  processingId={processingId}
                  nested
                />
              </div>
            )}
            {!hasChildren && (
              <div className='relative flex min-w-0 gap-2'>
                <CraRequirementItem
                  item={item}
                  validationItem={validationItems.get(item.id)}
                  isProcessing={processingId === item.id}
                />
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

export function CraUserInformationPage() {
  const { t } = useTranslation('craCompliance')
  const { productId = '', craStudioId = '' } = useParams()
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null
  )
  const requirementsQuery = useGetCraUserInformation(craStudioId)
  const { data: sources = [], isLoading: isLoadingSources } =
    useListProductSources(productId)
  const {
    isValidating,
    error: validationError,
    processingId,
    validationProgress,
    validationItems,
    validate,
  } = useValidateCraUserInformation(
    craStudioId,
    selectedDocumentId ?? undefined
  )

  const documentOptions = useMemo(() => {
    return sources
      .filter((source) => source.ingestionStatus === 'COMPLETED')
      .map((source) => {
        let label = source.name
        if (source.classification) {
          const classificationLabel = source.classification.replace(/_/g, ' ')
          label = `${label} (${classificationLabel})`
        }
        return { label, value: source.id }
      })
  }, [sources])

  const hasDocuments = sources.length > 0
  const showSelectionHint =
    !isLoadingSources && hasDocuments && !selectedDocumentId
  const showEmptyHint = !isLoadingSources && !hasDocuments
  let documentPlaceholder = t('userInformation.selectDocument')
  if (isLoadingSources) {
    documentPlaceholder = t('userInformation.loadingDocuments')
  }
  const isDocumentSelected = Boolean(selectedDocumentId)
  let buttonLabel = t('userInformation.selectDocumentButton')
  if (isDocumentSelected) {
    buttonLabel = t('userInformation.validate')
  }
  let buttonTitle: string | undefined
  if (!isDocumentSelected) {
    buttonTitle = t('userInformation.selectDocumentHint')
  }
  const requirements = requirementsQuery.data ?? []
  const showRequirementsLoading = requirementsQuery.isLoading
  const showRequirementsError =
    !requirementsQuery.isLoading && requirementsQuery.isError
  const showRequirements =
    !requirementsQuery.isLoading && !requirementsQuery.isError

  return (
    <div>
      <ComplianceSubPageHeader>
        <ComplianceSubPageTitle>
          {t('nav.userInformation')}
        </ComplianceSubPageTitle>
      </ComplianceSubPageHeader>

      <div className='space-y-4'>
        <div className='space-y-2'>
          <label
            htmlFor='cra-user-information-document'
            className='text-sm leading-none font-medium'
          >
            {showSelectionHint && t('userInformation.selectDocumentHint')}
          </label>
          <Select
            value={selectedDocumentId ?? ''}
            onValueChange={(value) => {
              if (value) {
                setSelectedDocumentId(value)
              }
            }}
            required
            items={documentOptions}
          >
            <SelectTrigger
              id='cra-user-information-document'
              className='w-full max-w-md'
            >
              <SelectValue placeholder={documentPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              {documentOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showEmptyHint && (
            <p className='text-sm text-muted-foreground'>
              {t('userInformation.noSourcesHint')}
            </p>
          )}
        </div>

        {showRequirementsLoading && (
          <div className='flex h-48 items-center justify-center rounded-lg border border-dashed'>
            <Loader2 className='size-8 animate-spin text-muted-foreground' />
          </div>
        )}

        {showRequirementsError && (
          <Alert variant='destructive'>
            <AlertCircle className='size-4' />
            <AlertDescription>
              {requirementsQuery.error.message ||
                t('userInformation.loadRequirementsFailed')}
            </AlertDescription>
          </Alert>
        )}

        {showRequirements && (
          <Card>
            <CardHeader className='mb-2 flex flex-row flex-wrap items-center justify-between gap-4'>
              <div className='min-w-0 flex-1'>
                <CardTitle className='text-base wrap-break-word'>
                  {t('userInformation.cardTitle')}
                </CardTitle>
                <p className='mt-1 text-sm text-muted-foreground'>
                  {t('userInformation.cardDescription')}
                </p>
              </div>
              <div className='flex shrink-0 items-center gap-4'>
                {validationProgress && (
                  <span className='text-sm font-medium text-muted-foreground'>
                    {validationProgress.current} / {validationProgress.total}
                  </span>
                )}
                <Button
                  onClick={() => {
                    if (!isDocumentSelected) {
                      return
                    }
                    void validate()
                  }}
                  disabled={isValidating || !isDocumentSelected}
                  title={buttonTitle}
                  size='sm'
                >
                  {isValidating && (
                    <span className='flex items-center gap-2'>
                      <span className='size-4 animate-spin rounded-full border-2 border-current border-t-transparent' />
                      {buttonLabel}
                    </span>
                  )}
                  {!isValidating && buttonLabel}
                </Button>
              </div>
            </CardHeader>
            {validationError && (
              <Alert variant='destructive' className='mb-4'>
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}
            <RequirementList
              items={requirements}
              validationItems={validationItems}
              processingId={processingId}
            />
          </Card>
        )}
      </div>
    </div>
  )
}
